import React from "react";
import { usePageTitle } from "../hooks/usePageTitle";

const Privacy = () => {
  usePageTitle("Privacy Policy");

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <h1 className="text-4xl font-bold text-white mb-6">Privacy Policy</h1>
      <p className="text-gray-400 text-sm mb-8">
        Last updated: Febuary 18, 2026
      </p>

      <div className="space-y-8 text-gray-300">
        <section>
          <h2 className="text-2xl font-semibold text-white mb-3">
            1. Introduction
          </h2>
          <p>
            Welcome to Collect Arena ("we," "our," or "us"). We respect your
            privacy and are committed to protecting your personal data. This
            privacy policy will inform you about how we handle your personal
            data when you visit our website and tell you about your privacy
            rights.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-3">
            2. Information We Collect
          </h2>
          <p className="mb-3">We collect and process the following data:</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>
              <strong>Account Information:</strong> Username, email address, and
              password (encrypted)
            </li>
            <li>
              <strong>Usage Data:</strong> Your quiz progress, completed lists,
              and guesses
            </li>
            <li>
              <strong>Gameplay Analytics:</strong> Anonymous aggregate
              statistics (e.g., how often items are guessed across all users) to
              improve content quality and balance list difficulty
            </li>
            <li>
              <strong>Technical Data:</strong> IP address, browser type, device
              information
            </li>
            <li>
              <strong>Cookies:</strong> Essential cookies for authentication and
              user preferences
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-3">
            3. Activity Tracking
          </h2>
          <p className="mb-3">
            To improve our service and provide analytics to administrators, we
            track:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>
              <strong>User Activity Timestamps:</strong> We record when you last
              interacted with the platform to show administrators how many users
              are currently active
            </li>
            <li>
              <strong>Privacy Note:</strong> Activity timestamps are only used
              for anonymous aggregate statistics (e.g., "5 users online now")
              and are never shared publicly or used to track individual behavior
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-3">
            4. Leaderboards and Public Data
          </h2>
          <p className="mb-3">
            Collect Arena features public leaderboards that display player
            performance:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>
              <strong>What's Displayed:</strong> Your username, completed lists
              count, correct answers count, and fastest completion times for
              specific lists
            </li>
            <li>
              <strong>Privacy Control:</strong> You can opt out of leaderboards
              at any time in your Profile settings. Changes take effect during
              the next daily update at midnight UTC
            </li>
            <li>
              <strong>Data Updates:</strong> Global leaderboards (Completed
              Lists, Correct Answers) update daily at midnight UTC. Fastest
              completion times update in real-time
            </li>
            <li>
              <strong>Persistent Records:</strong> Your fastest completion times
              are preserved even if you reset your progress on a list, allowing
              you to improve your personal records
            </li>
            <li>
              <strong>Moderation:</strong> Administrators may remove users from
              leaderboards for violations of our Terms of Service. Banned users
              can appeal by contacting{" "}
              <a
                href="mailto:privacy@collectarena.com"
                className="text-blue-400 hover:text-blue-300"
              >
                privacy@collectarena.com
              </a>
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-3">
            5. How We Use Your Information
          </h2>
          <p className="mb-3">We use your data to:</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Provide and maintain our service</li>
            <li>Track your quiz progress and save your achievements</li>
            <li>Improve our platform and user experience</li>
            <li>Send important service updates (if applicable)</li>
            <li>Protect against fraud and abuse</li>
            <li>
              Display your performance on public leaderboards (unless you opt
              out)
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-3">
            6. Legal Basis for Processing (GDPR)
          </h2>
          <p className="mb-3">
            We process your data under the following legal bases:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>
              <strong>Consent:</strong> You have given consent for specific
              purposes
            </li>
            <li>
              <strong>Contract:</strong> Processing is necessary to provide our
              service
            </li>
            <li>
              <strong>Legitimate Interests:</strong> To improve our platform and
              prevent fraud
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-3">
            7. Data Sharing
          </h2>
          <p>
            We do NOT sell, trade, or share your personal data with third
            parties. Your data is stored securely on our servers and is only
            accessible to authorized personnel for service maintenance.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-3">
            8. Your Rights (GDPR)
          </h2>
          <p className="mb-3">Under GDPR, you have the right to:</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>
              <strong>Access:</strong> Request a copy of your personal data
            </li>
            <li>
              <strong>Rectification:</strong> Correct inaccurate or incomplete
              data
            </li>
            <li>
              <strong>Erasure:</strong> Request deletion of your data ("right to
              be forgotten")
            </li>
            <li>
              <strong>Data Portability:</strong> Receive your data in a
              machine-readable format
            </li>
            <li>
              <strong>Withdraw Consent:</strong> Withdraw consent at any time
            </li>
            <li>
              <strong>Object:</strong> Object to processing based on legitimate
              interests
            </li>
          </ul>
          <p className="mt-3">
            To exercise these rights, please contact us at{" "}
            <a
              href="mailto:privacy@collectarena.com"
              className="text-blue-400 hover:text-blue-300"
            >
              privacy@collectarena.com
            </a>
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-3">
            9. Data Retention
          </h2>
          <p>
            We retain your personal data only as long as necessary to provide
            our services and comply with legal obligations. When you delete your
            account, all personal data is permanently removed from our systems
            within 30 days.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-3">
            10. Data Security
          </h2>
          <p>
            We implement appropriate technical and organizational measures to
            protect your data, including encryption, secure servers, and regular
            security audits. However, no method of transmission over the
            internet is 100% secure.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-3">
            11. Children's Privacy
          </h2>
          <p>
            Our service is not directed to individuals under the age of 13. We
            do not knowingly collect personal data from children under 13. If
            you believe we have collected data from a child, please contact us
            immediately.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-3">
            12. Changes to This Policy
          </h2>
          <p>
            We may update this privacy policy from time to time. We will notify
            you of any changes by posting the new policy on this page and
            updating the "Last updated" date.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-3">
            13. Contact Us
          </h2>
          <p>
            If you have questions about this privacy policy or our data
            practices, please contact us at:{" "}
            <a
              href="mailto:privacy@collectarena.com"
              className="text-blue-400 hover:text-blue-300"
            >
              privacy@collectarena.com
            </a>
          </p>
        </section>
      </div>
    </div>
  );
};

export default Privacy;
